import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, Briefcase, LineChart, Mail } from "lucide-react";

const CARDS = [
  {
    id: "creator",
    icon: Sparkles,
    label: "Creator Partnerships",
    desc: "For journalists, analysts, educators and operators building a durable audience on premium media rails.",
    cta: "Apply as creator",
    subject: "Creator Partnership Inquiry",
  },
  {
    id: "brand",
    icon: Briefcase,
    label: "Brand Partnerships",
    desc: "Premium adjacency to high-intent audiences. Lift-based attribution. Brand-safe by design.",
    cta: "Brand inquiry",
    subject: "Brand Partnership Inquiry",
  },
  {
    id: "investor",
    icon: LineChart,
    label: "Investor Inquiries",
    desc: "Building category-defining infrastructure for the future of premium media. Selective conversations.",
    cta: "Investor relations",
    subject: "Investor Inquiry",
  },
];

const EMAIL = "info@f3mediainc.com";

export default function Contact() {
  return (
    <section
      id="contact"
      data-testid="section-contact"
      className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden"
    >
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-violet-600/10 blur-[160px] pointer-events-none" />
      <div className="absolute inset-0 grid-bg-fine grid-mask opacity-25 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <div className="text-[11px] font-mono tracking-[0.3em] text-violet-300 uppercase mb-6">
            Chapter 07 · Contact
          </div>
          <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-white tracking-tight leading-[1.0]">
            Join the future of
            <br />
            <span className="text-zinc-400">premium media</span>
          </h2>
          <p className="mt-6 text-zinc-400 text-base md:text-lg leading-relaxed">
            We're selective about who we build with. If your work is signal,
            we want to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10 mb-16">
          {CARDS.map((c, i) => {
            const Icon = c.icon;
            const href = `mailto:${EMAIL}?subject=${encodeURIComponent(c.subject)}`;
            return (
              <motion.a
                key={c.id}
                href={href}
                data-testid={`contact-card-${c.id}`}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                className="group bg-black hover:bg-zinc-950 transition-colors p-8 md:p-10 flex flex-col"
              >
                <div className="flex items-center justify-between mb-8">
                  <div className="w-11 h-11 border border-white/15 flex items-center justify-center group-hover:border-violet-500/60 transition-colors">
                    <Icon className="w-5 h-5 text-zinc-300 group-hover:text-violet-300" strokeWidth={1.5} />
                  </div>
                  <span className="font-mono text-[10px] tracking-[0.25em] text-zinc-600">
                    0{i + 1} / 03
                  </span>
                </div>
                <div className="font-heading text-2xl text-white mb-3 tracking-tight">{c.label}</div>
                <p className="text-sm text-zinc-400 leading-relaxed flex-1">{c.desc}</p>
                <div className="mt-8 pt-5 border-t border-white/10 flex items-center justify-between text-[12px] font-mono uppercase tracking-[0.2em] text-white">
                  <span>{c.cta}</span>
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
                </div>
              </motion.a>
            );
          })}
        </div>

        {/* Direct email CTA */}
        <div className="border border-white/10 bg-black/40 backdrop-blur-sm p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-2">
              Direct line
            </div>
            <div className="font-serif-display text-2xl md:text-3xl text-white">
              {EMAIL}
            </div>
          </div>
          <a
            href={`mailto:${EMAIL}`}
            data-testid="contact-direct-email"
            className="inline-flex items-center gap-2 bg-violet-700 text-white px-6 py-3.5 text-sm font-medium hover:bg-violet-600 transition-colors"
          >
            <Mail className="w-4 h-4" strokeWidth={2} />
            Write to F3
          </a>
        </div>
      </div>
    </section>
  );
}
